import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { useSidebar } from '../../context/SidebarContext';
import { useBreadcrumb } from '../../context/BreadcrumbContext';

import {
  HeaderRow,
  PageWrapper,
  PaginationFooter,
  StatusTag,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
} from './styles';
import CustomTableHeader from '../../components/CustomHeader';
import CustomPagination from '../../components/CustomPagination';
import DetailModal from '../../components/DetailModal'
import { Box, TableRow, Typography, Checkbox, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {
  fetchOrgFacilityPendingShifts,
  fetchShiftDetails,
  selectOrgFacilityPendingShiftsLists,
  selectOrgFacilityPendingShiftsLoading,
  selectOrgFacilityPendingShiftsError,
  selectOrgFacilityPendingShiftsCount,
  bulkOrganizationApproveAttendance,
  selectUpdateShiftAttendanceLoading,
  selectUpdateShiftAttendanceSuccess,
  IShift,
  IATTENDANCE_MANAGER
} from '../../modules/shift';

import ShiftDetailContent from './components/ShiftDetailContent';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import AlertPopup from '../../components/common/AlertPopup';

type Order = 'asc' | 'desc';

interface Column {
  id: keyof IShift | string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const COLUMN_MAPPING: { [key: string]: keyof IShift | string } = {
  'ID No': 'shift_id',
  'Date': 'start_date_time',
  'Start Time': 'start_date_time',
  'End Time': 'end_date_time',
  'Facility': 'facility',
  'Position': 'position_role',
  'No. of Workers': 'shift_assignee_count',
  'Appoint Member': 'assigned_workers',
  'Status': 'status',
};

const FACILITY_SHIFT_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
  id: id,
  label: label,
  numeric: id === 'shift_id',
  disableSorting: !(id === 'title' || id === 'shift_id'),
}));


const OrgFacilityPendingShifts: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setIsShow } = useBreadcrumb();
  useEffect(() => {
    setActivePage('facility_pending_shifts');
    setIsShow(false);
  }, [setActivePage, setIsShow]);

  const dispatch = useDispatch<any>();
  const facilityShifts = useSelector(selectOrgFacilityPendingShiftsLists);
  const facilityShiftsLoading = useSelector(selectOrgFacilityPendingShiftsLoading);
  const facilityShiftsError = useSelector(selectOrgFacilityPendingShiftsError);
  const facilityShiftsCount = useSelector(selectOrgFacilityPendingShiftsCount);

  const [page, setPage] = useState(1);
  const rowsPerPage = 20;
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IShift | string>('title');
  const [shiftDetails, setShiftDetails] = useState<IShift | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Selection state
  const [selectedShiftIds, setSelectedShiftIds] = useState<number[]>([]);

  const bulkLoading = useSelector(selectUpdateShiftAttendanceLoading);
  const bulkSuccess = useSelector(selectUpdateShiftAttendanceSuccess);

  const [alert, setAlert] = useState<{ open: boolean, message: string, severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleOpenDrawer = () => setDrawerOpen(true);
  const handleCloseDrawer = () => setDrawerOpen(false);

  useEffect(() => {
    dispatch(fetchOrgFacilityPendingShifts({ page, pageSize: rowsPerPage }));
  }, [dispatch, page, rowsPerPage]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property as keyof IShift | string;

    const columnDef = FACILITY_SHIFT_COLUMNS.find(col => col.id === property);
    if (columnDef?.disableSorting) {
      return;
    }

    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  const getStatus = (status: string) => {
    if (status === 'started' || status === 'partially_started') return 'Active';
    if (status === 'partially_assigned' || status === 'assigned') return 'Scheduled';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'completed') return 'Completed';
    if (status === 'partially_completed') return 'Partially Completed';
    if (status === 'scheduled') return 'Pending';
    if (status === 'rejected') return 'Rejected';
    if (status === 'partially_rejected') return 'Partially Rejected';
    return status;
  }

  const getAttendanceManagerName = (manager: IATTENDANCE_MANAGER) => {
    if (!manager) return '-';
    return `${manager.first_name} ${manager.last_name}`;
  }

  const onClickRow = (shift: IShift) => {
    setShiftDetails(shift);
    handleOpenDrawer();
  };

  const handleRefresh = async () => {
    dispatch(fetchOrgFacilityPendingShifts({ page, pageSize: rowsPerPage }));
    if (shiftDetails) {
      try {
        const result = await dispatch(fetchShiftDetails(shiftDetails.shift_id)).unwrap();
        setShiftDetails(result as unknown as IShift);
      } catch (e) {
        console.error("Failed to refresh shift details", e);
      }
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const currentPageIds = facilityShifts.map((n) => n.shift_id);
      setSelectedShiftIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
      return;
    }
    const currentPageIds = facilityShifts.map((n) => n.shift_id);
    setSelectedShiftIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
  };

  const handleRowCheckboxClick = (event: React.MouseEvent<unknown>, id: number) => {
    event.stopPropagation();
    const selectedIndex = selectedShiftIds.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedShiftIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedShiftIds.slice(1));
    } else if (selectedIndex === selectedShiftIds.length - 1) {
      newSelected = newSelected.concat(selectedShiftIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedShiftIds.slice(0, selectedIndex),
        selectedShiftIds.slice(selectedIndex + 1),
      );
    }
    setSelectedShiftIds(newSelected);
  };

  const isSelected = (id: number) => selectedShiftIds.indexOf(id) !== -1;

  const handleBulkApprove = () => {
    if (selectedShiftIds.length === 0) return;
    setConfirmOpen(true);
  };

  const onConfirmBulkApprove = () => {
    setConfirmOpen(false);
    dispatch(bulkOrganizationApproveAttendance({ shift_ids: selectedShiftIds }))
      .unwrap()
      .then((res: any) => {
        setAlert({ open: true, message: res.message || 'Shifts approved successfully', severity: 'success' });
        setSelectedShiftIds([]);
        handleRefresh();
      })
      .catch((err: any) => {
        setAlert({ open: true, message: err || 'Failed to approve shifts', severity: 'error' });
      });
  };

  useEffect(() => {
    if (bulkSuccess) {
      // Logic handled in .then() of unwrap above
    }
  }, [bulkSuccess]);

  return (
    <PageWrapper>
      <Box sx={{ width: '100%', padding: 3 }}>
        <HeaderRow>
          <Typography variant="h5" style={{ fontWeight: 'bold', color: '#222' }}>
            Manage Shifts
          </Typography>
          {selectedShiftIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={bulkLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleBulkApprove}
              disabled={bulkLoading}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
            >
              Approve Selected ({selectedShiftIds.length})
            </Button>
          )}
        </HeaderRow>
        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={FACILITY_SHIFT_COLUMNS}
              order={order}
              orderBy={orderBy as string}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              numSelected={selectedShiftIds.filter(id => facilityShifts.some(s => s.shift_id === id)).length}
              rowCount={facilityShifts.length}
            />
            <StyledTableBody>
              {facilityShiftsLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={FACILITY_SHIFT_COLUMNS.length + 1} align="center">
                    Loading...
                  </StyledTableCell>
                </TableRow>
              ) : facilityShiftsError || !facilityShifts || !facilityShifts.length ? (
                <TableRow>
                  <StyledTableCell colSpan={FACILITY_SHIFT_COLUMNS.length + 1} align="center">
                    No data available
                  </StyledTableCell>
                </TableRow>
              ) : (
                facilityShifts.map((shift) => (
                  <TableRow
                    key={shift.shift_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => onClickRow(shift)}
                    selected={isSelected(shift.shift_id)}
                  >
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isSelected(shift.shift_id)}
                        onClick={(event) => handleRowCheckboxClick(event, shift.shift_id)}
                        sx={{ color: 'inherit' }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{shift.shift_id}</StyledTableCell>
                    <StyledTableCell>{dayjs(shift.start_date_time).format('DD/MM/YYYY')}</StyledTableCell>
                    <StyledTableCell>{dayjs(shift.start_date_time).format('hh:mm A')}</StyledTableCell>
                    <StyledTableCell>{dayjs(shift.end_date_time).format('hh:mm A')}</StyledTableCell>
                    <StyledTableCell>{typeof shift.facility === 'object' ? (shift.facility as any).facility_name : '-'}</StyledTableCell>
                    <StyledTableCell>{shift.position_role}</StyledTableCell>
                    <StyledTableCell>{shift.shift_assignee_count} / {shift.workers_required}</StyledTableCell>
                    <StyledTableCell>{getAttendanceManagerName(shift.attendance_manager)}</StyledTableCell>
                    <StyledTableCell>
                      <StatusTag status={getStatus(shift.status)}>
                        {getStatus(shift.status)}
                      </StatusTag>
                    </StyledTableCell>
                  </TableRow>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>
        <PaginationFooter>
          <Box>
            Showing {facilityShifts?.length || 0} of {facilityShiftsCount} results
          </Box>
          <CustomPagination
            currentPage={page}
            totalPages={Math.ceil(facilityShiftsCount / rowsPerPage)}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </PaginationFooter>
      </Box>
      {shiftDetails &&
        <DetailModal
          open={drawerOpen}
          onClose={handleCloseDrawer}
          shiftId={shiftDetails.shift_id}
          shiftStatus={getStatus(shiftDetails.status)}
        >
          <ShiftDetailContent
             shiftId={shiftDetails.shift_id}
             onRefresh={handleRefresh}
          />
        </DetailModal>
      }
      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirmBulkApprove}
        title="Confirm Bulk Approval"
        message={`Are you sure you want to approve the attendance for ${selectedShiftIds.length} selected shifts?`}
        loading={bulkLoading}
      />
      <AlertPopup
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </PageWrapper>
  );
};

export default OrgFacilityPendingShifts;